import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtArr, fmtSignedPct } from "../format";
import type { GrowthRateData } from "./GrowthRateCard";

/** Combined YoY + MoM subscription-ARR growth, anchored to the last complete
 *  month. Replaces the two separate growth tiles with one card. */
export function GrowthCard({
  anchorLabel,
  yoy,
  mom,
}: {
  anchorLabel: string;
  yoy: GrowthRateData | null;
  mom: GrowthRateData | null;
}) {
  return (
    <Card className="relative overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-1 bg-brand" />
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        ARR Growth — {anchorLabel}
      </div>

      <div className="mt-2 flex gap-6">
        <Metric label="YoY" data={yoy} />
        <Metric label="MoM" data={mom} />
      </div>

      <div className="mt-3 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        BI.REVENUE_WATERFALL · subscription MRR × 12 · last complete month
      </div>
    </Card>
  );
}

function Metric({ label, data }: { label: string; data: GrowthRateData | null }) {
  const pct = data?.pct;
  const delta = data?.deltaArr;
  const tone =
    pct == null ? "text-muted-foreground" : pct >= 0 ? "text-success" : "text-danger";
  return (
    <div>
      <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
        {label}
      </div>
      <div className={cn("text-2xl font-extrabold leading-tight tracking-tight tabular-nums", tone)}>
        {pct != null ? fmtSignedPct(pct) : "—"}
      </div>
      <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
        {delta != null ? (
          <>
            {delta >= 0 ? "↑" : "↓"} {fmtArr(Math.abs(delta), 0)}
          </>
        ) : (
          "—"
        )}
      </div>
      {data?.comparisonLabel && (
        <div className="text-[10px] text-muted-foreground/70">{data.comparisonLabel}</div>
      )}
    </div>
  );
}
